/* global o_util, ServiceWorkerRegistration, o_apps */
"use strict";

window.o_global = window.o_global || {};

/**
 * Global API for browser push handling.
 */
o_global.push = (function() {
    // Private "constants" and declarations.
    var WORKER_URL = "/static/push-service/js/latest/serviceworker/sw.js",
        CHANNELS = {
            "PRICE_ALERT": "mz_preisalarm" // Channel name for price alert

        },
        PERMISSION = { // Native browser permissions
            // "DEFAULT": "default",
            // "DENIED": "denied",
            "GRANTED": "granted"
        },
        PUSH_SERVER_PATH = "/assets-push-service/register",
        CHANNEL_KEY = "o_pushChannels", // LocalStorage key for channels
        storage = new o_global.Storage(function() {
            return window.localStorage;
        });

    /**
     * Sets the "pushEnabled"-Cookie if push notifications are available.
     * Check will always be false if script is running in app context.
     *
     * @private
     */
    function _setPushStatus() {
        if (!o_util.cookie.exists("app") &&
            ("serviceWorker" in navigator) &&
            ("showNotification" in ServiceWorkerRegistration.prototype) &&
            ("PushManager" in window)) {
            o_util.cookie.set("pushEnabled", "true");
        } else {
            o_util.cookie.set("pushEnabled", "false");
        }
    }

    /**
     * Returns the value of the pushEnabledCookie.
     * If it is not available it will be set.
     *
     * @return {Boolean} Are push notification enabled?
     */
    function isEnabled() {
        if (!o_util.cookie.exists("pushEnabled")) {
            _setPushStatus();
        }

        return o_util.cookie.get("pushEnabled") === "true";
    }

    /**
     * Extracts the subscribed push channels from LocalStorage
     * and returns them as array object.
     *
     * @private
     *
     * @return {Array} Array of subscribed push channels.
     */
    function _getChannels() {
        return JSON.parse(storage.getItem(CHANNEL_KEY)) || [];
    }

    /**
     * Adds a new channel to the LocalStorage array.
     *
     * @param {String} channel  The channel.
     *
     * @private
     *
     * @return {Boolean} Indicator if channel could be pushed and saved into LocalStorage.
     */
    function _addChannel(channel) {
        if (typeof channel !== "string" || channel.length <= 0) {
            throw new Error("[Push] invalid channel: " + channel);
        }

        var channels = storage.getItem(CHANNEL_KEY),
            parsed;

        if (typeof channels === "string") { // Array exists
            parsed = JSON.parse(channels);

            // Check if channel already exists in storage
            for (var index = 0; index < parsed.length; ++index) {
                if (parsed[index] === channel) {
                    return true;
                }
            }

            parsed.push(channel);

            return storage.setItem(CHANNEL_KEY, JSON.stringify(parsed));
        } else { // Array does not exist in storage
            return storage.setItem(CHANNEL_KEY, JSON.stringify([channel]));
        }
    }

    /**
     * Removes a channel from the LocalStorage.
     *
     * @param {String} channel          The channel.
     *
     * @private
     *
     * @return {Boolean} Indicator if channel could be pushed and saved into LocalStorage.
     */
    function _removeChannel(channel) {
        var channels = storage.getItem(CHANNEL_KEY),
            parsed;

        if (typeof channels === "string") {
            try {
                parsed = JSON.parse(channels);

                for (var index = 0; index < parsed.length; ++index) {
                    if (parsed[index] === channel) {
                        parsed.splice(index, 1);
                        break;
                    }
                }

                return storage.setItem(CHANNEL_KEY, JSON.stringify(parsed));
            } catch (e) {
                if (o_global.debug.status().activated) {
                    console.error("Channel read/write exception", e);
                }

                return false;
            }
        }
    }

    /**
     * Encodes and wraps the payload before sending the data.
     *
     * @param {Object} payload    Payload as JSON object.
     *
     * @private
     *
     * @return {String} Decoded payload.
     */
    function _preparePayload(payload) {
        return JSON.stringify({
            "data": window.btoa(JSON.stringify(payload))
        });
    }

    /**
     * Registers the push token and channel(s) to adobe campaign (Ipanema)
     * via assets browser push service.
     *
     * @see https://qspa.otto.de/confluence/pages/viewpage.action?pageId=181381921
     *
     * @param {String} pushToken    Push token of subscription.
     *
     * @private
     */
    function _registerToIpanema(pushToken) {
        return new Promise(function(resolve, reject) {
            var payload = _preparePayload({
                    "registrationToken": pushToken, // Client subscription token / push token
                    "channels": _getChannels(), // Subscription channels,
                    "osName": AS && AS.RUM && typeof AS.RUM.getDeviceData === "function" ?
                        AS.RUM.getDeviceData().deviceOS :
                        undefined
                }),
                options = {
                    "url": PUSH_SERVER_PATH,
                    "method": "POST",
                    "data": payload,
                    "contentType": "application/json"
                };

            if (o_global.debug.status().activated) {
                console.log("[Push] sending data to Ipanema", JSON.parse(window.atob(JSON.parse(payload).data)));
            }

            o_util.ajax(options, function(xhr) {
                if (xhr.status === 200) {
                    resolve();

                    return;
                }

                reject(new Error("[Push] invalid data sent"));
            });
        });
    }

    /**
     * Extracts the push token of a subscription endpoint.
     *
     * @param {PushSubscription} subscription   A PushSubscription object.
     *
     * @private
     *
     * @return {String} The extracted push token.
     */
    function _extractPushToken(subscription) {
        // The subscription.endpoint attribute contains something like:
        // https://android.googleapis.com/gcm/send/f0w3g3P5wnw:APA91bFTqATzi...
        // and the "push token" we need is the part behind ../gcm/send/
        return subscription.endpoint.split("/").slice(-1)[0];
    }

    /**
     * Checks on a service worker registration
     * if subscription exists or requests a new one.
     *
     * @private
     *
     * @param {ServiceWorkerRegistration} registration  A worker registration object.
     *
     * @return {Promise} Resolves with a PushSubscription object or rejects with error.
     */
    function _subscribe(registration) {
        return new Promise(function(resolve, reject) {
            registration.pushManager.getSubscription()
                .then(function(subscription) {
                    if (!subscription) {
                        return registration.pushManager.subscribe({
                            "userVisibleOnly": true // Currently this must be set.
                        });
                    } else {
                        resolve(subscription);
                    }
                })
                .then(resolve) // Resolve with subscription
                .catch(reject);
        });
    }

    /**
     * Checks the current permission explicitly for valid and given values.
     * Requests a new permission if value is `default` or `denied`.
     * Rejects with error when denied.
     *
     * @private
     *
     * @return {Promise} A promise.
     */
    function _requestNotificationPermission() {
        return new Promise(function(resolve, reject) {
            var permission = Notification.permission;

            // Check the current permission explicitly for valid and given values.
            // Request a new permission if value is `default` or `denied`.
            if (permission === PERMISSION.GRANTED) {
                resolve(permission);
            } else {
                Notification.requestPermission(function(result) {
                    if (result === PERMISSION.GRANTED) {
                        resolve(result);
                    } else {
                        reject(new Error("[Push] permission denied"));
                    }
                });
            }
        });
    }

    /**
     * Returns the service worker url with current
     * directory hash.
     *
     * @private
     *
     * @return {String} The service worker url;
     */
    function _getWorkerUrl() {
        if (o_global.dirhashes && o_global.dirhashes.js && o_global.dirhashes.js["push-service"]) {
            return WORKER_URL.replace("latest", o_global.dirhashes.js["push-service"]);
        }

        return WORKER_URL;
    }

    /**
     * Sends a log request (Kibana).
     *
     * @param {String}  namespace   Log namespace.
     * @param {String}  message     Log message.
     * @private
     */
    function _sendLog(namespace, message) {
        if (!!window.AS && !!window.AS.RUM && !!window.AS.RUM.sendCustomRequest &&
            typeof window.AS.RUM.sendCustomRequest === "function") {
            window.AS.RUM.sendCustomRequest(namespace, {
                "message": message
            });
        }
    }

    /**
     * Requests a browser notification permission and registers a given
     * `channel` to Google Cloud Messaging (Ipanema). Activate debugging
     * to get more detailed information (`o_global.debug.activate();`).
     *
     * @param {String} channel          The channel which needs a permission
     * @param {String} frontendToken    The related token or ID (e.g. price alert button id).
     * @param {String} cbGranted        Callback when permission request and registration was successful.
     * @param {String} cbNotGranted     Callback when it was not successful or another error occurred.
     */
    function grantPushPermission(channel, frontendToken, cbGranted, cbNotGranted) {
        var appContext = o_util.cookie.exists("app");

        if (!appContext && isEnabled()) { // No app context and push available
            _requestNotificationPermission()
                .then(function() {
                    return _addChannel(channel);
                })
                .then(function() {
                    return navigator.serviceWorker.register(_getWorkerUrl());
                })
                .then(_subscribe)
                .then(_extractPushToken)
                .then(_registerToIpanema)
                .then(function() {
                    if (typeof cbGranted === "function") {
                        cbGranted(frontendToken);
                    }
                })
                .catch(function(error) {
                    if (o_global.debug.status().activated) {
                        console.error(error);
                    }

                    // Log all errors
                    _sendLog("browserPushError", error.toString() + " | " + location.href + " | " + navigator.userAgent);

                    // Cleanup localstorage
                    _removeChannel(channel);

                    if (typeof cbNotGranted === "function") {
                        cbNotGranted(frontendToken);
                    }
                });
        } else if (appContext) { // App context
            if (channel === CHANNELS.PRICE_ALERT) {
                o_apps.grantPriceAlertPermission(frontendToken, cbGranted, cbNotGranted);
            }
        } else {
            if (typeof cbNotGranted === "function") {
                cbNotGranted(frontendToken);
            }
        }
    }

    // Instant call to write the `pushEnabled` cookie.
    isEnabled();

    // Public API.
    return {
        "isEnabled": isEnabled,
        "grantPushPermission": grantPushPermission,
        "_private": { // Only for testing!
            "_getWorkerUrl": _getWorkerUrl,
            "_extractPushToken": _extractPushToken,
            "_preparePayload": _preparePayload,
            "_removeChannel": _removeChannel,
            "_addChannel": _addChannel,
            "_getChannels": _getChannels
        }
    };
}());
